import React, { Fragment } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Logger } from "lib/logger";
import type { CMSPageModule } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils";
import type { StyleProps, GridProps } from "@chakra-ui/react";
import type { ImageProps } from "next/image";
import { Grid } from "components/Grid";
import { ProductCard } from "components/ProductCard";
import type { PLPMidPageBannerType, VariantType } from "@shared/graphql.types";
import { MidPageBanner } from "./MidPageBanner";
import type { SegmentTrackProductClickedEvent } from "@beautypie/segment/Track/ProductClicked";

type Props = {
  variants: VariantType[];
  mt?: StyleProps["mt"];
  gap?: StyleProps["gap"];
  showPrices?: boolean;
  showQuickShopButton?: boolean;
  rowGap?: StyleProps["rowGap"];
  gridTemplateColumns?: GridProps["templateColumns"];
  setConfirmationVariant?: React.Dispatch<
    React.SetStateAction<VariantType | undefined>
  >;
  setConfirmation?: React.Dispatch<React.SetStateAction<boolean>>;
  trackSearch?: (slug: string) => void;
  trackViewedVariant?: (variantSku: string) => void;
  onOpen?: () => void;
  blocks?: PLPMidPageBannerType[];
  trackingModule?: Omit<CMSPageModule, "moduleClickUrl">;
  searchTerm?: string;
  resultType?: SegmentTrackProductClickedEvent["properties"]["result_type"];
} & Pick<ImageProps, "objectFit" | "priority">;

export const ProductCardGrid: React.FC<Props> = ({
  variants,
  mt = 1,
  showQuickShopButton = false,
  showPrices = true,
  gridTemplateColumns = {
    base: "repeat(2, 1fr)",
    lg: "repeat(3, 1fr)",
    xl: "repeat(4, 1fr)"
  },
  gap = 2,
  rowGap = {
    base: 5,
    lg: 10
  },
  setConfirmationVariant,
  setConfirmation,
  trackSearch,
  trackViewedVariant,
  onOpen,
  blocks,
  trackingModule,
  resultType,
  searchTerm,
  ...imageProps
}) => {
  const showBreakoutBanner = variants.length >= 8 && !!blocks?.length;

  return (
    <Grid
      gridTemplateColumns={gridTemplateColumns}
      noGridItem
      gap={gap}
      rowGap={rowGap}
      mt={mt}
      // 1 item for small phones and 2 items for portrait tablets
      css={{
        "@media (max-width: 374px)": {
          gridTemplateColumns: "1fr"
        },
        "@media (min-width: 768px) and @media (max-width: 900px)": {
          gridTemplateColumns: "2fr"
        }
      }}
    >
      {variants.map((variant, index) => (
        <Fragment key={`${variant.id}-${variant.sku}`}>
          <ErrorBoundary
            FallbackComponent={() => null}
            onError={(error) =>
              Logger.warn(`PLP breakout banner failed to load. ${error}`)
            }
          >
            {index === 6 && showBreakoutBanner && (
              <MidPageBanner
                banners={[
                  blocks[0] as PLPMidPageBannerType,
                  blocks[1] as PLPMidPageBannerType | undefined,
                  blocks[2] as PLPMidPageBannerType | undefined
                ]}
                onOpen={onOpen}
              />
            )}
          </ErrorBoundary>
          <ProductCard
            variant={variant}
            position={index}
            showPrices={showPrices}
            showQuickShopButton={showQuickShopButton}
            setConfirmationVariant={setConfirmationVariant}
            setConfirmation={setConfirmation}
            trackingModule={trackingModule}
            resultType={resultType}
            searchTerm={searchTerm}
            {...(trackSearch && { trackSearch })}
            {...(trackViewedVariant && { trackViewedVariant })}
            {...imageProps}
          />
        </Fragment>
      ))}
    </Grid>
  );
};
